/**
 * 地图右栏的「设备」页签:选中一台设备时的实时态势。
 *
 * ★ 这是原来那个「指挥舱」页仅有的、地图没有的东西 —— 而指挥舱本身不在产品规格的
 *   信息架构里(spec-fleet-console.md 定义 10 页, 没有它), 接上真数据之后它和地图页
 *   几乎逐像素相同。所以把有价值的这一块搬到它本来该在的地方:位置相关的东西在地图页
 *   (规格 §⑧ 自己写着「位置信息在 ⑤ 地图页」), 名单与令牌管理在设备页。
 *
 * ★★ 这里【不放任何控制按钮】。原座舱右栏有"急停/返航", 两个都没有 onClick ——
 *    看起来完全可用、按下去什么都不发生, 在安全控件上这是最坏的一种。
 *    而且它们本就不该在云端:会让狗动的操作要人在现场(CLAUDE.md §0.4)。
 */
import type { Terminal } from '@/types/fleet';
import type { HubLivePose } from '@/lib/hubClient';
import type { MapDevice } from './MapFrameView';
import { fmtAge, poseIsStale } from '@/lib/poseAge';

interface Props {
  terminals: Terminal[];
  /** 画在当前这张图上的那些设备(已按 mapId 过滤)。 */
  devices: MapDevice[];
  livePoses: Record<string, HubLivePose>;
  selected: string | null;
  onSelect: (id: string | null) => void;
  /** 这张图看过的格子占比 —— 规格 §⑤ 的"覆盖率视图", 目前只有当前值没有历史。 */
  gridKnown: number | null;
}

export default function DevicePanel({ terminals, devices, livePoses, selected, onSelect, gridKnown }: Props) {
  const onMap = new Set(devices.map((d) => d.id));
  const active = terminals.find((t) => t.id === selected) ?? null;
  const pose = devices.find((d) => d.id === selected) ?? null;
  const nowS = Date.now() / 1000;

  return (
    <div className="space-y-3">
      {gridKnown != null && (
        <div className="px-2.5 py-2 rounded-md bg-background-50 border border-background-200/70">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-foreground-500">这张图已观测</span>
            <span className="font-mono text-foreground-800">{(gridKnown * 100).toFixed(1)}%</span>
          </div>
          {/* 覆盖率只有"此刻", 没有增长曲线 —— 那需要按时间归档, 现在没有。 */}
          <p className="text-[10px] text-foreground-400 mt-0.5">分母 = 栅格全部格子;没有历史曲线(需按时间归档)</p>
        </div>
      )}

      <ul className="space-y-1.5">
        {terminals.map((t) => {
          const live = livePoses[t.id];
          const p = devices.find((d) => d.id === t.id);
          const isSel = t.id === selected;
          return (
            <li key={t.id}>
              <button onClick={() => onSelect(isSel ? null : t.id)}
                className={`w-full text-left p-2.5 rounded-md border transition-all cursor-pointer ${
                  isSel ? 'bg-primary-500/10 border-primary-300/60' : 'bg-background-50 border-background-200/70 hover:border-background-300/60'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-medium text-foreground-800 truncate">{t.name}</span>
                  <span className={`inline-flex items-center gap-1 text-[10px] ${t.status === 'online' ? 'text-accent-600' : 'text-foreground-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'online' ? 'bg-accent-500' : 'bg-foreground-300'}`} />
                    {t.status === 'online' ? '在线' : '离线'}
                  </span>
                </div>
                <div className="text-[10px] text-foreground-400 mt-0.5 font-mono">
                  {p
                    ? <>{p.x.toFixed(1)}, {p.y.toFixed(1)} m · {live ? '实时' : '遥测'} {fmtAge(nowS - p.poseAt)}前</>
                    : onMap.has(t.id)
                      ? '位置未知'
                      /* 规格 §⑤ 明确要求能表达"某终端位置未知"(眼镜就没有位姿),
                         所以这里说的是原因, 不是把它从名单里藏掉。 */
                      : <span title="定位不在时设备不推位姿 —— 缺席比一个旧坐标诚实">不在这张图上 / 未报位姿</span>}
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {active && (
        <div className="p-2.5 rounded-md bg-background-50 border border-background-200/70 space-y-1.5">
          <h4 className="text-[11px] font-semibold text-foreground-800">{active.name} · 实时态势</h4>
          {/* 测不到的一律画 —— 不画 0(§9b)。 */}
          <Row label="电量" value={active.battery != null ? `${active.battery}%` : null} />
          <Row label="温度" value={active.temperature != null ? `${active.temperature}°C` : null} />
          <Row label="CPU / GPU" value={active.cpuUsage != null ? `${active.cpuUsage}% / ${active.gpuUsage ?? '—'}%` : null} />
          <Row label="版本" value={active.version} mono />
          <Row label="接入" value={active.networkType ? `${active.networkType} ${active.overlayIP ?? ''}` : null} mono />
          {pose && (
            <>
              <Row label="坐标" value={`${pose.x.toFixed(2)}, ${pose.y.toFixed(2)} m`} mono />
              <Row label="朝向" value={pose.yaw != null ? `${((pose.yaw * 180) / Math.PI).toFixed(0)}°` : null} mono />
              <div className="flex justify-between text-[10px]">
                <span className="text-foreground-500">位姿</span>
                <span className={`font-mono ${poseIsStale(pose.poseAt, nowS) ? 'text-amber-600' : 'text-accent-600'}`}>
                  {livePoses[active.id] ? '实时流' : '遥测'} · {fmtAge(nowS - pose.poseAt)}前
                </span>
              </div>
            </>
          )}
          {active.services && (
            <div className="flex justify-between text-[10px]">
              <span className="text-foreground-500">组件</span>
              <span className="font-mono text-foreground-700">
                {Object.values(active.services).filter(Boolean).length}/{Object.keys(active.services).length} 在跑
              </span>
            </div>
          )}
          <p className="text-[10px] text-foreground-400 pt-1 leading-relaxed">
            云端只看不控:急停、返航这类会让狗动的操作要人在现场下发(§0.4)。画面在「视频墙」。
          </p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex justify-between text-[10px]">
      <span className="text-foreground-500">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} ${value ? 'text-foreground-800' : 'text-foreground-400'}`}>
        {value || '待上报'}
      </span>
    </div>
  );
}
